import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import React from 'react';

const SEOContext = createContext();

// Default SEO data
const DEFAULT_SEO = {
  title: "Steel B2B Pro – India's Trusted B2B Steel Trading Platform",
  description: "Steel B2B Pro is a leading B2B steel trading platform in India offering TMT Bars, HR Coils, CR Sheets, Plates, Pipes at best prices. Get instant quotes from verified suppliers.",
  keywords: "B2B steel trading, steel supplier India, TMT bars price, HR coils supplier, steel marketplace, bulk steel, steel dealer India",
  canonical: "https://steelb2bpro.com",
  ogType: "website",
  ogImage: "/og-image.jpg"
};

// Organization Schema
const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Steel B2B Pro",
  "description": "India's trusted B2B steel trading platform for reliable sourcing and transparent pricing",
  "url": "https://steelb2bpro.com",
  "logo": "https://steelb2bpro.com/logo.png",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+91-8340346279",
    "contactType": "sales",
    "email": "admin@steelb2bpro.com",
    "areaServed": "IN",
    "availableLanguage": ["English", "Hindi"]
  },
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Jamshedpur",
    "addressRegion": "Jharkhand",
    "addressCountry": "IN"
  }
};

// Local Business Schema
const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Steel B2B Pro",
  "description": "B2B Steel Trading Platform - Suppliers of TMT Bars, HR Coils, CR Sheets, Plates, Pipes & all steel products",
  "url": "https://steelb2bpro.com",
  "telephone": "+91-8340346279",
  "email": "admin@steelb2bpro.com",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Jamshedpur",
    "addressRegion": "Jharkhand",
    "postalCode": "831001",
    "addressCountry": "IN"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "22.8046",
    "longitude": "86.2029"
  },
  "openingHours": "Mo-Sa 09:00-18:00",
  "priceRange": "$$",
  "areaServed": {
    "@type": "Country",
    "name": "India"
  }
};

export function SEOProvider({ children }) {
  const [seo, setSEO] = useState(DEFAULT_SEO);
  const [schemas, setSchemas] = useState([ORGANIZATION_SCHEMA, LOCAL_BUSINESS_SCHEMA]);

  // Update document head
  useEffect(() => {
    // Update title
    document.title = seo.title;
    
    // Update meta tags
    updateMetaTag('description', seo.description);
    updateMetaTag('keywords', seo.keywords);
    
    // Update Open Graph tags
    updateMetaTag('og:title', seo.title, 'property');
    updateMetaTag('og:description', seo.description, 'property');
    updateMetaTag('og:type', seo.ogType, 'property');
    updateMetaTag('og:url', seo.canonical, 'property');
    updateMetaTag('og:image', seo.ogImage, 'property');
    updateMetaTag('og:site_name', 'Steel B2B Pro', 'property');
    
    // Update Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', seo.title);
    updateMetaTag('twitter:description', seo.description);
    
    // Update canonical
    updateCanonical(seo.canonical);
    
    // Update schemas
    updateSchemas(schemas);
    
  }, [seo, schemas]);

  const updateMetaTag = (name, content, attr = 'name') => {
    let element = document.querySelector(`meta[${attr}="${name}"]`);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attr, name);
      document.head.appendChild(element);
    }
    element.setAttribute('content', content);
  };

  const updateCanonical = (url) => {
    let element = document.querySelector('link[rel="canonical"]');
    if (!element) {
      element = document.createElement('link');
      element.setAttribute('rel', 'canonical');
      document.head.appendChild(element);
    }
    element.setAttribute('href', url);
  };

  const updateSchemas = (schemaList) => {
    // Remove existing schema scripts
    document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    
    // Add new schemas
    schemaList.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  };

  const updateSEO = useCallback((newSEO) => {
    setSEO(prev => ({ ...prev, ...newSEO }));
  }, []);

  const addSchema = useCallback((schema) => {
    setSchemas(prev => [...prev, schema]);
  }, []);

  const setProductSEO = useCallback((product) => {
    if (!product) return;
    
    const productName = product.name || product.full_name;
    const category = product.category_name || product.category;
    
    // Generate SEO-optimized title
    const title = `${productName} - Best Price in India | Steel B2B Pro`;
    
    // Generate meta description
    const description = `Buy ${productName} at best prices in India. ${
      product.grades ? `Available grades: ${product.grades.slice(0, 3).join(', ')}. ` : ''
    }${
      product.sizes ? `Sizes: ${product.sizes[0]} to ${product.sizes[product.sizes.length - 1]}. ` : ''
    }Get instant quotes from verified suppliers. PAN India delivery.`;
    
    // Generate keywords
    const keywords = [
      productName,
      `${productName} price`,
      `${productName} supplier`,
      `${productName} India`,
      `buy ${productName}`,
      category,
      'steel supplier',
      'B2B steel'
    ].join(', ');
    
    updateSEO({
      title,
      description: description.slice(0, 160),
      keywords,
      canonical: `https://steelb2bpro.com/catalogue?product=${product.id}`,
      ogType: 'product'
    });
    
    // Add Product schema
    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": productName,
      "description": description,
      "category": category,
      "brand": {
        "@type": "Brand",
        "name": "Steel B2B Pro"
      },
      "offers": {
        "@type": "AggregateOffer",
        "priceCurrency": "INR",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Steel B2B Pro"
        }
      }
    };
    
    // Add Breadcrumb schema
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://steelb2bpro.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Catalogue",
          "item": "https://steelb2bpro.com/catalogue"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": category,
          "item": `https://steelb2bpro.com/catalogue?category=${category?.toLowerCase().replace(/\s+/g, '_')}`
        },
        {
          "@type": "ListItem",
          "position": 4,
          "name": productName,
          "item": `https://steelb2bpro.com/catalogue?product=${product.id}`
        }
      ]
    };
    
    setSchemas([ORGANIZATION_SCHEMA, LOCAL_BUSINESS_SCHEMA, productSchema, breadcrumbSchema]);
    
  }, [updateSEO]);

  const setCategorySEO = useCallback((category) => {
    if (!category) return;
    
    const categoryName = category.name || category;
    
    const title = `${categoryName} - Steel Products at Best Prices | Steel B2B Pro`;
    const description = `Buy ${categoryName} at best prices in India. Wide range of grades and sizes available. Get instant quotes from verified suppliers. PAN India delivery.`;
    
    updateSEO({
      title,
      description,
      keywords: `${categoryName}, ${categoryName} price, ${categoryName} supplier, steel products India`,
      canonical: `https://steelb2bpro.com/catalogue?category=${categoryName.toLowerCase().replace(/\s+/g, '_')}`,
      ogType: 'website'
    });
    
  }, [updateSEO]);

  const resetSEO = useCallback(() => {
    setSEO(DEFAULT_SEO);
    setSchemas([ORGANIZATION_SCHEMA, LOCAL_BUSINESS_SCHEMA]);
  }, []);

  return (
    <SEOContext.Provider value={{
      seo,
      updateSEO,
      addSchema,
      setProductSEO,
      setCategorySEO,
      resetSEO
    }}>
      {children}
    </SEOContext.Provider>
  );
}

export function useSEO() {
  const context = useContext(SEOContext);
  if (!context) {
    throw new Error('useSEO must be used within a SEOProvider');
  }
  return context;
}

// SEO Head Component - Renders hidden SEO content
export function SEOHead({ 
  title, 
  description, 
  keywords,
  faqs = [],
  h1,
  h2Sections = [],
  contentSections = {},
  product = null
}) {
  const { updateSEO } = useSEO();

  // Update SEO only once when component mounts or when these specific values change
  useEffect(() => {
    if (title || description || keywords) {
      updateSEO({ title, description, keywords });
    }
    // Only run on mount and when these values change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords]);

  // Render hidden but crawlable content
  return (
    <div className="sr-only" aria-hidden="false" style={{ 
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: '0',
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      border: '0'
    }}>
      {h1 && <h1>{h1}</h1>}
      
      {h2Sections.map((section, idx) => (
        <section key={idx}>
          <h2>{section}</h2>
          {contentSections[section.toLowerCase().replace(/\s+/g, '_')] && (
            <p>{contentSections[section.toLowerCase().replace(/\s+/g, '_')]}</p>
          )}
        </section>
      ))}
      
      {Object.entries(contentSections).map(([key, content]) => (
        <div key={key}>
          <h3>{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</h3>
          <p>{content}</p>
        </div>
      ))}
      
      {faqs.length > 0 && (
        <section>
          <h2>Frequently Asked Questions</h2>
          {faqs.map((faq, idx) => (
            <div key={idx} itemScope itemType="https://schema.org/Question">
              <h3 itemProp="name">{faq.question}</h3>
              <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                <p itemProp="text">{faq.answer}</p>
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

export default SEOContext;
